/**
 * ACCESS and WORK ROLES, edited separately.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT THIS REPLACES
 * ═══════════════════════════════════════════════════════════════════════════
 * A single dropdown that turned one role INTO another. Making Andy — an owner
 * — also a technician meant changing him from OWNER to TECHNICIAN, which took
 * his access away. Making a technician also a detailer was not expressible at
 * all.
 *
 * So the editor has two sections that do not touch each other:
 *
 *   ACCESS      one value. What this person may administer.
 *   WORK ROLES  a set. What this person does. Independently additive and
 *               removable — removing Detail Specialist from a technician who
 *               details leaves Technician exactly where it was.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE CLIENT IS NOT THE AUTHORITY
 * ═══════════════════════════════════════════════════════════════════════════
 * Saving calls `manage-membership-roles`, which re-checks everything: that the
 * actor's authority reaches this shop, that they cannot hand out a capability
 * they lack, and that nobody raises their own. Disabling a control here is a
 * courtesy so people are not offered actions that will bounce — it is not the
 * enforcement, and the server refuses regardless of what this component sends.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * FORM FACTOR
 * ═══════════════════════════════════════════════════════════════════════════
 * A full-screen sheet on a phone and a centred dialog from sm up, because a
 * two-section editor inside a table cell is unusable on a 375px screen. Save
 * is a full-width sticky control on a phone rather than a corner button, and
 * it is never hidden by viewport.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Loader2, Shield, Wrench } from 'lucide-react';
import { WorkRoleMultiSelect } from './WorkRoleMultiSelect';
import {
  AUTHORITY_LABELS, resolveMembershipRoles,
  type AuthorityRole, type WorkRole,
} from '@/lib/roles/multiRole';
import { AUTHORITY_CAPABILITIES, assignableAuthoritiesFor } from '@/lib/roles/authority';

export interface MemberRolesEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: {
    userId: string;
    displayName: string;
    authority: AuthorityRole;
    workRoles: readonly WorkRole[];
  } | null;
  /** The person doing the editing, for the courtesy checks below. */
  actor: { userId: string; authority: AuthorityRole } | null;
  saving?: boolean;
  /**
   * ONLY THE HALVES THIS ACTOR ACTUALLY CHANGED.
   *
   * Both fields are optional, and an omitted one means "leave it alone" all
   * the way down to the database. See the mutation-contract note above.
   */
  onSave: (next: { authorityRole?: AuthorityRole; workRoles?: WorkRole[] }) => void | Promise<void>;
}

export function MemberRolesEditor({
  open, onOpenChange, member, actor, saving = false, onSave,
}: MemberRolesEditorProps) {
  const [authority, setAuthority] = useState<AuthorityRole>('STAFF');
  const [workRoles, setWorkRoles] = useState<WorkRole[]>([]);

  useEffect(() => {
    if (!member) return;
    setAuthority(member.authority);
    setWorkRoles([...member.workRoles]);
  }, [member]);

  const actorCaps = useMemo(
    () => new Set(actor ? AUTHORITY_CAPABILITIES[actor.authority] ?? [] : []),
    [actor],
  );

  const mayEditAuthority = actorCaps.has('manage_authority');
  const mayEditWorkRoles = actorCaps.has('manage_work_roles');

  /**
   * Authorities this actor could actually grant.
   *
   * Two rules, not one. "You cannot hand out a capability you do not hold" was
   * the only one applied here, and it let DISTRICT_MANAGER and REGION_MANAGER
   * through — their capabilities ARE a subset of an owner's — even though the
   * server refuses both outright with `authority_scope_unsupported` because
   * their scope is not representable. The editor was offering an owner two
   * choices that could only bounce.
   *
   * `assignableAuthoritiesFor` applies both rules, in the canonical module, so
   * the deferred set is named once rather than restated as strings here.
   */
  const assignableAuthorities = useMemo(
    () => (actor ? assignableAuthoritiesFor(actor.authority) : []),
    [actor],
  );

  /**
   * ═════════════════════════════════════════════════════════════════════════
   * THE MUTATION CONTRACT — send what you CHANGED, not what you SAW
   * ═════════════════════════════════════════════════════════════════════════
   * Save used to emit BOTH halves unconditionally:
   *
   *     onSave({ authorityRole: authority, workRoles })
   *
   * For an OWNER that is harmless. For a MANAGER it made the editor unusable.
   * A manager holds `manage_work_roles` and NOT `manage_authority`, so the
   * access control above is correctly disabled for them — but Save still sent
   * `authority_role`, the server saw an authority field present, read that as
   * a request to CHANGE authority, and refused the whole save with
   * `manage_authority required`. The manager was shown working work-role
   * controls that could never be saved.
   *
   * The server is right and stays untouched: a field that is present is a
   * mutation. The client's job is to stop claiming mutations it did not make.
   *
   * So each half is included only when the actor may edit it AND it actually
   * differs from what the member has now. A manager adding Detail Specialist
   * sends a work-role mutation alone; an owner changing both sends both.
   */
  const workRolesChanged = useMemo(() => {
    if (!member) return false;
    const before = [...member.workRoles].sort().join('|');
    const after = [...workRoles].sort().join('|');
    return before !== after;
  }, [member, workRoles]);

  const authorityChanged = !!member && authority !== member.authority;

  /** Exactly the mutation this actor is entitled to make, and nothing more. */
  const pendingChange = useMemo(() => {
    const next: { authorityRole?: AuthorityRole; workRoles?: WorkRole[] } = {};
    if (mayEditAuthority && authorityChanged) next.authorityRole = authority;
    if (mayEditWorkRoles && workRolesChanged) next.workRoles = [...workRoles];
    return next;
  }, [mayEditAuthority, authorityChanged, authority, mayEditWorkRoles, workRolesChanged, workRoles]);

  const hasChanges = Object.keys(pendingChange).length > 0;

  const isSelf = !!actor && !!member && actor.userId === member.userId;
  const wouldElevateSelf = useMemo(() => {
    if (!isSelf || !member) return false;
    const current = new Set(AUTHORITY_CAPABILITIES[member.authority] ?? []);
    return (AUTHORITY_CAPABILITIES[authority] ?? []).some((c) => !current.has(c));
  }, [isSelf, member, authority]);

  if (!member) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Full-bleed on a phone, centred dialog from sm up. */}
      <DialogContent
        className="max-h-[100dvh] w-full max-w-none overflow-y-auto rounded-none sm:max-h-[90vh] sm:max-w-lg sm:rounded-lg"
        data-testid="member-roles-editor"
      >
        <DialogHeader>
          <DialogTitle>Roles for {member.displayName}</DialogTitle>
          <DialogDescription>
            Access and work roles are separate. Changing what someone does will
            not change what they can administer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* ── ACCESS ───────────────────────────────────────────────────── */}
          <section className="space-y-2" data-testid="member-access-section">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-muted-foreground" />
              <Label htmlFor="member-authority" className="text-sm font-medium">Access</Label>
            </div>
            <p className="text-xs text-muted-foreground">
              What this person may administer. One value.
            </p>
            <Select
              value={authority}
              onValueChange={(v) => setAuthority(v as AuthorityRole)}
              disabled={!mayEditAuthority || saving}
            >
              <SelectTrigger id="member-authority" className="w-full" data-testid="member-authority-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {assignableAuthorities.map((r) => (
                  <SelectItem key={r} value={r}>{AUTHORITY_LABELS[r]}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {!mayEditAuthority && (
              <p className="text-xs text-muted-foreground" data-testid="member-authority-locked">
                You do not have permission to change access levels.
              </p>
            )}
            {wouldElevateSelf && (
              <p className="text-xs text-destructive" data-testid="member-self-elevation-warning">
                You cannot raise your own access level.
              </p>
            )}
          </section>

          <Separator />

          {/* ── WORK ROLES ───────────────────────────────────────────────── */}
          <section className="space-y-2" data-testid="member-work-roles-section">
            <div className="flex items-center gap-2">
              <Wrench className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Work Roles</span>
            </div>
            <WorkRoleMultiSelect
              value={workRoles}
              onChange={setWorkRoles}
              disabled={!mayEditWorkRoles || saving}
              label=""
              description="What this person does. Add or remove each one independently."
              idPrefix="member-work-role"
            />
            {!mayEditWorkRoles && (
              <p className="text-xs text-muted-foreground" data-testid="member-work-roles-locked">
                You do not have permission to change work roles.
              </p>
            )}
          </section>
        </div>

        {/* Full-width stacked on a phone; inline from sm up. */}
        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onSave(pendingChange)}
            disabled={saving || wouldElevateSelf || !hasChanges}
            className="w-full sm:w-auto"
            data-testid="member-roles-save"
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save roles
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Short summary for a list row: "Owner · Technician, Detail Specialist". */
export function describeMemberRoles(m: {
  role?: string | null;
  authority_role?: string | null;
  work_roles?: readonly string[] | null;
}): string {
  const { authority, workRoles } = resolveMembershipRoles(m);
  const jobs = workRoles.length
    ? workRoles.map((r) => ({
        TECHNICIAN: 'Technician',
        SERVICE_ADVISOR: 'Service Advisor',
        DISPATCHER: 'Dispatcher',
        PARTS: 'Parts',
        DETAIL_SPECIALIST: 'Detail Specialist',
      }[r])).join(', ')
    : 'No work roles';
  return `${AUTHORITY_LABELS[authority]} · ${jobs}`;
}

export default MemberRolesEditor;
